/**
 * Request a password reset (CLAUDE.md 11.6; REVIEW-001 P1-4).
 *
 * The page **always** shows the same confirmation, whether or not an account exists.
 * That mirrors the endpoint, and for the same reason: any difference here turns the
 * form into an account-enumeration oracle, and this is one of the few things an
 * unauthenticated stranger can submit freely.
 *
 * So the success state is worded to be true either way: "if that address has an
 * account, a link is on its way".
 */
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { MailCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError, api } from '@/api/client'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'

export function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  const [identifier, setIdentifier] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      await api.publicPost('/auth/password/forgot', { identifier })
      setSent(true)
    } catch (caught) {
      // A rate limit is the only failure the user can act on; everything else is
      // deliberately indistinguishable from success.
      setError(caught instanceof ApiError ? caught.localised : String(caught))
    } finally {
      setPending(false)
    }
  }

  if (sent) {
    return (
      <AuthLayout title={t('password.forgot_title')}>
        <div className="space-y-4 text-center">
          <MailCheck size={32} className="mx-auto text-[var(--status-pass)]" aria-hidden />
          <p role="status" className="text-sm">
            {t('password.forgot_sent')}
          </p>
          <Button asChild variant="secondary" className="w-full">
            <Link to="/login">{t('login.title')}</Link>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={t('password.forgot_title')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && (
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <Field description={t('password.forgot_hint')}>
          <FieldLabel>{t('login.identifier')}</FieldLabel>
          <FieldInput
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" className="w-full" loading={pending}>
          {t('action.submit', { ns: 'common' })}
        </Button>

        <p className="text-center text-sm">
          <Link to="/login" className="text-accent hover:underline">
            {t('login.title')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}
